import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { store } from '@store/index'
import { logout } from '@store/slices/authSlice'
import { addNotification } from '@store/slices/appSlice'

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = store.getState().auth.token

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add request timestamp for debugging
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      console.log(
        `[API Request] ${config.method?.toUpperCase()} ${config.url}`,
        {
          data: config.data,
          params: config.params,
        }
      )
    }

    return config
  },
  error => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful responses in debug mode
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      console.log(
        `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
        {
          status: response.status,
          data: response.data,
        }
      )
    }

    return response
  },
  error => {
    const { response } = error

    // Handle different error scenarios
    if (response) {
      const { status, data } = response

      switch (status) {
        case 401:
          // Unauthorized - clear auth and redirect to login
          store.dispatch(logout())
          store.dispatch(
            addNotification({
              type: 'error',
              title: 'Authentication Error',
              message: 'Your session has expired. Please log in again.',
            })
          )
          break

        case 403:
          // Forbidden (usually means account is inactive/deactivated)
          const userRole = store.getState().auth.user?.role
          store.dispatch(logout()); // <--- Add this line to force logout
          // Redirect based on user role
          if (userRole === 'client') {
            window.location.href = '/patient-login'
          } else if (userRole === 'therapist') {
            window.location.href = '/provider-login'
          } else {
            window.location.href = '/patient-login'
          }
          store.dispatch(
            addNotification({
              type: 'error',
              title: 'Account Inactive',
              message: 'Your account has been deactivated. Please contact support.',
            })
          );
          break;

        case 404:
          // Not found
          store.dispatch(
            addNotification({
              type: 'error',
              title: 'Not Found',
              message: data.message || 'The requested resource was not found.',
            })
          )
          break

        case 422:
          // Validation error
          const validationErrors = data.errors || {}
          const errorMessages = Object.values(validationErrors)
            .flat()
            .join(', ')
          store.dispatch(
            addNotification({
              type: 'error',
              title: 'Validation Error',
              message:
                errorMessages || 'Please check your input and try again.',
            })
          )
          break

        case 500:
          // Server error
          store.dispatch(
            addNotification({
              type: 'error',
              title: 'Server Error',
              message:
                'An internal server error occurred. Please try again later.',
            })
          )
          break

        default:
          // Generic error
          store.dispatch(
            addNotification({
              type: 'error',
              title: 'Error',
              message: Array.isArray(data?.message)
                ? data.message.join(', ')
                : data?.message || 'An unexpected error occurred.',
            })
          )
      }
    } else if (error.request) {
      // Network error
      store.dispatch(
        addNotification({
          type: 'error',
          title: 'Network Error',
          message:
            'Unable to connect to the server. Please check your internet connection.',
        })
      )
    } else {
      // Other error
      store.dispatch(
        addNotification({
          type: 'error',
          title: 'Error',
          message: error.message || 'An unexpected error occurred.',
        })
      )
    }

    console.error('[API Response Error]', error)
    return Promise.reject(error)
  }
)

// Membership Plans API functions
export const membershipApi = {
  // Apply coupon to membership plan price
  getCoupon: async (couponCode: string, amount: number) => {
    const response = await api.get(
      `/membership/get-coupon/${couponCode}?amount=${amount}`
    )
    return response.data // expected: { finalAmount: number }
  },

  // Get membership plans
  getPlans: async (page: number = 1, limit: number = 10) => {
    const response = await api.get(
      `/membership/get-plans?page=${page}&limit=${limit}`
    )
    return response.data
  },

  // Get membership subscription prices for a specific plan
  getMembershipSubscriptionPrice: async (planId: string) => {
    const response = await api.get(
      `/membership/get-membership-subscription-price/${planId}`
    )
    return response.data
  },

  // Therapist signup - creates account and returns therapist ID
  therapistSignup: async (therapistData: {
    firstName: string
    lastName: string
    email: string
    password: string
    phone: string
    specialization?: string[]
    bio?: string
    gender?: string
    addressLine?: string
    city?: string
    state?: string
    zipCode?: string
    insuranceAccepted?: string[]
    degrees?: string[]
    yearsOfExperience?: number
    genderIdentity?: string
    pronouns?: string
    sessionFee?: number
    sessionLength?: number
    profilePhotoUrl?: string
    npiNumber?: string
    [key: string]: any
  }) => {
    const response = await api.post('/therapist/signup', therapistData)
    return response.data
  },

  // Create subscription - should be called after therapist signup
  createSubscription: async (subscriptionData: {
    therapist_id: string
    members_price: Array<{ price_id: string }>
    total_price: number
    amount_paid: number
    coupon?: string
    billing_address: {
      address1: string
      address2?: string
      city: string
      state: string
      postal_code: string
      country: string
    }
  }) => {
    console.log('subscriptionData', subscriptionData)
    const response = await api.post(
      '/membership/membership-plan-subscription-all-member',
      subscriptionData,
      {
        timeout: 0, // No timeout for subscription creation
      }
    )
    return response.data
  },

  // Initialize payment with Stripe - uses client_secret for client-side payment confirmation
  initializePayment: async (paymentData: {
    orderId: string
    client_secret: string
    customerId: string
    amount: number
  }) => {
    // This endpoint should handle Stripe payment intent confirmation on the backend
    const response = await api.post('/payment/initialize', paymentData)
    return response.data
  },

  // Connect therapist bank account through Stripe
  connectTherapistBank: async (email: string, returnEndpoint: string) => {
    // Use direct axios call to bypass interceptors for this specific endpoint
    // Using the correct base URL from your curl command
    const baseUrl = import.meta.env.VITE_API_BASE_URL
    const returnUrl = `${window.location.origin}${returnEndpoint}`

    const response = await axios.post(
      `${baseUrl}/therapist-subscriptions/connected-therapist`,
      {
        email: email,
        return_url: returnUrl,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,
      }
    )
    return response.data
  },

  // Get specialties list
  getSpecialties: async () => {
    const response = await api.get('/specilities/specility')
    return response.data
  },

  // Get payers list
  getPayers: async () => {
    const response = await api.get('/payers/all')
    return response.data
  },

  // Search payers with query parameter
  searchPayers: async (searchQuery: string) => {
    const response = await api.get(
      `/payers/all?search=${encodeURIComponent(searchQuery)}`
    )
    return response.data
  },

  // Board certification APIs (search as query param)
  getBoardNames: async (search?: string) => {
    const params = { search: search?.trim() || '' }
    const response = await api.get('/therapist/board-name', { params })
    return response.data
  },
  getBoardSpecialities: async (search?: string, boardId?: string) => {
    const params = { 
      search: search?.trim() || '',
      boardId: boardId || ''
    }
    const response = await api.get('/therapist/board-speciality', { params })
    return response.data
  },
  getBoardSubspecialities: async (search?: string, specialityId?: string) => {
    const params = { 
      search: search?.trim() || '',
      specialityId: specialityId || ''
    }
    const response = await api.get('/therapist/board-subspeciality', { params })
    return response.data
  },

  // Upload profile photo
  uploadProfilePhoto: async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post('/therapist/upload-profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // No timeout for large image uploads
    })
    return response.data
  },

  // Upload single license file
  uploadLicenseFile: async (file: File) => {
    const formData = new FormData()
    formData.append('files', file)

    // Endpoint expected to return { message, files: [{ fileName, signedUrl, location }] }
    const response = await api.post('/therapist/upload-multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // No timeout for large file uploads
    })
    return response.data
  },

  // Get therapist licenses list
  getLicenses: async (page: number = 1, limit: number = 10) => {
    const response = await api.get(
      `/therapist/license/list?page=${page}&limit=${limit}`
    )
    return response.data
  },

  getLicensesAdmin: async (page: number = 1, limit: number = 10) => {
    const response = await api.get(
      `/admin/license/list?page=${page}&limit=${limit}`
    )
    return response.data
  },

  // Delete therapist license
  deleteLicense: async (licenseId: string) => {
    const response = await api.delete(`/therapist/license/${licenseId}`)
    return response.data
  },

  // Update therapist license
  updateLicense: async (licenseId: string, licenseData: FormData) => {
    const response = await api.put(
      `/therapist/license/${licenseId}`,
      licenseData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  },

  deleteLicenseAdmin: async (licenseId: string) => {
    const response = await api.delete(`/admin/license/${licenseId}`)
    return response.data
  },

  // Update therapist license
  updateLicenseAdmin: async (licenseId: string, licenseData: FormData) => {
    const response = await api.put(
      `/admin/license/${licenseId}`,
      licenseData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  },

  // Add new therapist license
  addLicense: async (licenseData: FormData) => {
    const response = await api.post('/therapist/license/add', licenseData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  addLicenseCertificate: async (licenseData: FormData) => {
    const response = await api.post('/admin/license/add', licenseData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Get therapist services list
  getTherapistServices: async () => {
    const response = await api.get('/therapist/services/list')
    return response.data
  },

  // Toggle service visibility
  toggleServiceVisibility: async (serviceId: string, isVisible: boolean) => {
    const response = await api.patch(
      `/therapist/services/${serviceId}/toggle`,
      {
        isVisible,
      }
    )
    return response.data
  },

  // Get US states list
  getStates: async () => {
    const response = await api.get('/state/get-states')
    return response.data
  },

  // Get degrees list
  getDegrees: async () => {
    const response = await api.get('/specilities/degree')
    return response.data
  },

  // Get degrees by specialities
  getDegreesBySpecialities: async (specialityIds: string[]) => {
    const response = await api.post('/specilities/get-degree', {
      specialityIds,
    })
    return response.data
  },

  // Get specialty roles (license types)
  getSpecialtyRoles: async () => {
    const response = await api.get('/specilities/specility-roles')
    return response.data
  },

  // Get genders list
  getGenders: async () => {
    const response = await api.get('/services/genders')
    return response.data
  },

  // Get languages list
  getLanguages: async () => {
    const response = await api.get('/services/languages')
    return response.data
  },
}

// Therapist Sessions API functions
export const therapistSessionsApi = {
  // Get all therapist sessions (booked appointments)
  getTherapistSessions: async () => {
    const response = await api.get('/schedule-session/therapist-sessions')
    return response.data
  },

  // Get therapist upcoming sessions
  getTherapistUpcomingSessions: async () => {
    const response = await api.get(
      '/schedule-session/therapist-upcoming-sessions'
    )
    return response.data
  },
}

// Patients Sessions API functions
export const patientSessionsApi = {
  // Get all patient sessions (booked appointments)
  getTherapistSessions: async () => {
    const response = await api.get('/schedule-session/patient-sessions')
    return response.data
  },

  // Get patient upcoming sessions
  getTherapistUpcomingSessions: async () => {
    const response = await api.get(
      '/schedule-session/patient-upcoming-sessions'
    )
    return response.data
  },
}

// Schedule Exceptions API functions
export const scheduleExceptionsApi = {
  // Get all schedule exceptions for the authenticated therapist
  getExceptions: async () => {
    const response = await api.get('/therapist/schedule/exceptions')
    return response.data
  },

  // Add a new schedule exception
  addException: async (exceptionData: {
    type: 'date_range' | 'partial_day'
    start: string
    end: string
    reason?: string
  }) => {
    const response = await api.post(
      '/therapist/schedule/exceptions',
      exceptionData
    )
    return response.data
  },

  // Update an existing schedule exception
  updateException: async (
    exceptionId: string,
    exceptionData: {
      type?: 'date_range' | 'partial_day'
      start?: string
      end?: string
      reason?: string
    }
  ) => {
    const response = await api.put(
      `/therapist/schedule/exceptions/${exceptionId}`,
      exceptionData
    )
    return response.data
  },

  // Delete a schedule exception
  deleteException: async (exceptionId: string) => {
    const response = await api.delete(
      `/therapist/schedule/exceptions/${exceptionId}`
    )
    return response.data
  },

  // Check availability with exceptions applied
  checkAvailability: async (date: string) => {
    const response = await api.get(
      `/therapist/schedule/availability?date=${date}`
    )
    return response.data
  },
}

// Schedule Exceptions API functions
export const scheduleExceptionsPatientApi = {
  // Get all schedule exceptions for the authenticated therapist
  getExceptions: async () => {
    const response = await api.get('/therapist/schedule/exceptions')
    return response.data
  },

  // Add a new schedule exception
  addException: async (exceptionData: {
    type: 'date_range' | 'partial_day'
    start: string
    end: string
    reason?: string
  }) => {
    const response = await api.post(
      '/therapist/schedule/exceptions',
      exceptionData
    )
    return response.data
  },

  // Update an existing schedule exception
  updateException: async (
    exceptionId: string,
    exceptionData: {
      type?: 'date_range' | 'partial_day'
      start?: string
      end?: string
      reason?: string
    }
  ) => {
    const response = await api.put(
      `/therapist/schedule/exceptions/${exceptionId}`,
      exceptionData
    )
    return response.data
  },

  // Delete a schedule exception
  deleteException: async (exceptionId: string) => {
    const response = await api.delete(
      `/therapist/schedule/exceptions/${exceptionId}`
    )
    return response.data
  },

  // Check availability with exceptions applied
  checkAvailability: async (date: string) => {
    const response = await api.get(
      `/therapist/schedule/availability?date=${date}`
    )
    return response.data
  },
}

export default api
